// =====================================================================
// src/pages/intl-acca/LadderIntlPage.tsx
// =====================================================================
// LADDER-Intl surface (founder ruling #1 — separate from Focus/Value).
// Presentational only: renders R1–R5 rungs produced by the WC Agent
// Pipeline. Per-rung tier gating is decided by the route (route/UI
// layer pattern); locked rungs show a desire teaser, never the picks.
// Honest-empty until the pipeline assembles rungs (WC2026 ACTIVE +
// back-test GREEN).

import type { IntlAccaTournament } from "../../db/intl-acca-types";
import { formatOdds } from "../../db/intl-acca-types";
import type { LadderRung } from "../../intl-acca/useIntlAccaLadder.ts";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";

export interface LadderIntlPageProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  rungs: LadderRung[];
  matchLabels: Record<string, string>;
  /** rung_code → viewer may see the picks (route computes via tierMeets). */
  unlocked: Record<string, boolean>;
  onUpgrade?: () => void;
}

export function LadderIntlPage(props: LadderIntlPageProps): JSX.Element {
  const { loading, error, tournament, rungs, matchLabels, unlocked, onUpgrade } =
    props;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading the ladder…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState variant="custom" title="Ladder unavailable." body={error} />
      </div>
    );
  }

  return (
    <div className="ia-root">
      <header className="ia-page-header">
        <div>
          <div className="ia-eyebrow">
            International Acca Ladder
            {tournament ? ` · ${tournament.name}` : ""}
          </div>
          <h1>
            The Ladder.{" "}
            <em>Five rungs. One pricing engine.</em>
          </h1>
          <p className="ia-sub">
            R1 Starter through R5 Max — each rung assembled by the World
            Cup agent pipeline from CLV-positive, real-priced selections.
            Rungs populate only when the engine has genuine signal.
          </p>
        </div>
      </header>

      <div className="ia-page-pad">
        {rungs.length === 0 ? (
          <HonestEmptyState
            variant="custom"
            title="No rungs live yet."
            body="The ladder fills once the pipeline is validated (back-test green) and the tournament is active. Empty by design today — no fabricated rungs."
          />
        ) : (
          rungs.map((r) => {
            const open = unlocked[r.rung_code] ?? false;
            return (
              <div
                key={r.id}
                className="ia-card"
                style={{ marginBottom: 18, padding: 0 }}
              >
                <div
                  className="ia-standings-head"
                  style={{ justifyContent: "space-between" }}
                >
                  <span className="ia-eyebrow">
                    {r.rung_code} · {r.rung_label}
                  </span>
                  <span
                    className="ia-mono"
                    style={{
                      fontSize: "var(--sz-mono-sm)",
                      color: "var(--accent-text)",
                      letterSpacing: "0.14em",
                    }}
                  >
                    {r.combined_odds == null
                      ? `${r.target_min_odds}x+`
                      : `${formatOdds(r.combined_odds)}×`}{" "}
                    · {r.legs.length} legs
                  </span>
                </div>

                {open ? (
                  <div>
                    {r.legs.map((l) => (
                      <div
                        key={l.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--sz-space-4)",
                          padding: "var(--sz-space-3) var(--sz-space-5)",
                          borderTop: "1px solid var(--border-faint)",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "var(--sz-body-sm)", color: "var(--text)" }}>
                            {matchLabels[l.match_id] ?? l.match_id}
                          </div>
                          <div
                            className="ia-mono"
                            style={{
                              fontSize: 11,
                              color: "var(--text-tertiary)",
                              marginTop: 2,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {l.selection} · {l.reasoning_note}
                          </div>
                        </div>
                        <span
                          className="ia-mono"
                          style={{
                            fontSize: "var(--sz-mono-sm)",
                            color: "var(--text-secondary)",
                            textAlign: "right",
                            flexShrink: 0,
                          }}
                        >
                          {formatOdds(l.market_price_at_selection)}
                        </span>
                        <span
                          className="ia-mono"
                          style={{
                            fontSize: 10,
                            color: "var(--accent)",
                            letterSpacing: "0.12em",
                            flexShrink: 0,
                          }}
                        >
                          {l.clv_confidence}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "28px var(--sz-space-7)",
                      textAlign: "center",
                    }}
                  >
                    <div
                      className="ia-eyebrow"
                      style={{ marginBottom: 8 }}
                    >
                      ★ {r.rung_code} is a higher tier
                    </div>
                    <p
                      style={{
                        fontSize: "var(--sz-body-sm)",
                        color: "var(--text-secondary)",
                        maxWidth: 460,
                        margin: "0 auto 16px",
                      }}
                    >
                      {r.legs.length} CLV-positive legs at{" "}
                      {r.combined_odds == null
                        ? `${r.target_min_odds}x+`
                        : `${formatOdds(r.combined_odds)}×`}{" "}
                      combined. Upgrade to read every selection, edge,
                      and reasoning note.
                    </p>
                    <button
                      type="button"
                      onClick={onUpgrade}
                      className="ia-pill ia-pill-outline"
                      style={{ cursor: "pointer", padding: "10px 18px" }}
                    >
                      Upgrade to unlock {r.rung_code} →
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
